import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import PageHeader from '../../components/UI/PageHeader';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import ConfirmDialog from '../../components/UI/ConfirmDialog';
import {
  getUserHealthDevice,
  activateUserHealthDevice,
  deactivateUserHealthDevice,
  getUserById
} from '../../services/users/usersService';
import { HealthDeviceResponseDto, UserDto } from '../../services/users/types';

const DeviceDetails: React.FC = () => {
  const { id, deviceId } = useParams<{ id: string; deviceId: string }>();
  const auth = useAuth();
  const navigate = useNavigate();
  
  const [device, setDevice] = useState<HealthDeviceResponseDto | null>(null);
  const [user, setUser] = useState<UserDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // State for confirmation dialogs
  const [isConfirmActivateOpen, setIsConfirmActivateOpen] = useState(false);
  const [isConfirmDeactivateOpen, setIsConfirmDeactivateOpen] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0); // Used to force refresh
  
  // Fetch user details
  const fetchUserDetails = useCallback(async () => {
    if (!id || !auth.isAuthenticated) return;
    
    try {
      const userData = await getUserById(auth.user || null, id);
      setUser(userData);
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError('Failed to fetch user details.');
    }
  }, [id, auth.isAuthenticated, auth.user]);
  
  // Fetch device details
  const fetchDeviceDetails = useCallback(async () => {
    if (!id || !deviceId || !auth.isAuthenticated) {
      setError('Required parameters missing or user not authenticated.');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const deviceData = await getUserHealthDevice(auth.user || null, id, deviceId);
      setDevice(deviceData);
    } catch (err) {
      console.error('Error fetching device details:', err);
      setError('Failed to fetch device details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [id, deviceId, auth.isAuthenticated, auth.user]);
  
  // Fetch data on component mount and when dependencies change
  useEffect(() => {
    fetchUserDetails();
    fetchDeviceDetails();
  }, [fetchUserDetails, fetchDeviceDetails, refreshKey]);
  
  // Handle back button
  const handleBack = () => {
    navigate(`/users/${id}/devices`);
  };
  
  // Handle activation
  const handleActivate = async () => {
    if (!id || !deviceId) return;
    
    setIsActionLoading(true);
    
    try {
      await activateUserHealthDevice(auth.user || null, id, deviceId);
      setRefreshKey(prev => prev + 1); // Force refresh
      setIsConfirmActivateOpen(false);
    } catch (err) {
      console.error('Error activating device:', err);
      setError('Failed to activate device. Please try again.');
    } finally {
      setIsActionLoading(false);
    }
  };
  
  // Handle deactivation
  const handleDeactivate = async () => {
    if (!id || !deviceId) return;
    
    setIsActionLoading(true);
    
    try {
      await deactivateUserHealthDevice(auth.user || null, id, deviceId);
      setRefreshKey(prev => prev + 1); // Force refresh
      setIsConfirmDeactivateOpen(false);
    } catch (err) {
      console.error('Error deactivating device:', err);
      setError('Failed to deactivate device. Please try again.');
    } finally {
      setIsActionLoading(false);
    }
  };
  
  // Format date for display
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };
  
  // Get icon for device provider
  const getProviderIcon = (provider?: string) => {
    switch (provider) {
      case 'FREESTYLELIBRE':
        return 'fa-heartbeat';
      case 'GARMIN':
        return 'fa-running';
      case 'APPLE':
        return 'fa-apple';
      default:
        return 'fa-mobile-alt';
    }
  };
  
  return (
    <div>
      <PageHeader
        title={isLoading ? 'Loading...' : error ? 'Error' : !device ? 'Not Found' : device.provider ? `${device.provider} Device` : 'Device Details'}
        breadcrumbs={[
          { label: "Dashboard", to: "/" },
          { label: "Users", to: "/users" },
          { label: user?.username || id || 'User', to: `/users/${id}` },
          { label: "Devices", to: `/users/${id}/devices` },
          { label: isLoading ? 'Loading...' : error ? 'Error' : !device ? 'Not Found' : 'Details' }
        ]}
      />
      
      <Card>
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 text-red-700 p-4 rounded-md mb-4">
            {error}
            <Button 
              variant="ghost" 
              size="sm" 
              className="ml-2"
              onClick={() => setRefreshKey(prev => prev + 1)}
            >
              Retry
            </Button>
          </div>
        ) : !device ? (
          <div className="bg-yellow-50 text-yellow-700 p-4 rounded-md mb-4">
            Device not found
            <Button 
              variant="ghost" 
              size="sm" 
              className="ml-2"
              onClick={handleBack}
            >
              Back to Devices
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6">
            <div className="md:col-span-2">
              <div className="flex items-center mb-6">
                <div className="flex-shrink-0 h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center text-blue-500">
                  <i className={`fas ${getProviderIcon(device.provider)} text-2xl`}></i>
                </div>
                <div className="ml-4">
                  <h3 className="text-xl font-semibold">{device.provider || 'Health Device'}</h3>
                  <p className="text-gray-500">
                    {device.enabled ? (
                      <span className="text-green-600">
                        <i className="fas fa-check-circle mr-1"></i> Active
                      </span>
                    ) : (
                      <span className="text-red-600">
                        <i className="fas fa-times-circle mr-1"></i> Inactive
                      </span>
                    )}
                  </p>
                </div>
              </div>
              
              <h3 className="text-lg font-medium text-gray-900 mb-4 border-b border-gray-200 pb-2">Device Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <span className="text-sm font-medium text-gray-500 block">ID</span>
                  <span className="mt-1 block font-mono text-sm">{device.id || '-'}</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500 block">Language</span>
                  <span className="mt-1 block">{device.language || '-'}</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500 block">Created At</span>
                  <span className="mt-1 block">{formatDate(device.createdAt)}</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500 block">Updated At</span>
                  <span className="mt-1 block">{formatDate(device.updatedAt)}</span>
                </div>
              </div>
              
              {device.parameters && Object.keys(device.parameters).length > 0 && (
                <>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 border-b border-gray-200 pb-2">Parameters</h3>
                  <div className="bg-gray-50 p-4 rounded-md mb-6 overflow-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                            Key
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                            Value
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {Object.entries(device.parameters).map(([key, value]) => (
                          <tr key={key}>
                            <td className="px-3 py-2 whitespace-nowrap font-medium">
                              {key}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {value}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Actions</h3>
              <div className="space-y-3">
                <Button 
                  variant="primary" 
                  fullWidth
                  onClick={handleBack}
                >
                  <i className="fas fa-arrow-left mr-2"></i>
                  Back to Devices
                </Button>
                
                {device.enabled ? (
                  <Button 
                    variant="danger" 
                    fullWidth
                    onClick={() => setIsConfirmDeactivateOpen(true)}
                  >
                    <i className="fas fa-ban mr-2"></i>
                    Deactivate Device
                  </Button>
                ) : (
                  <Button 
                    variant="secondary" 
                    fullWidth
                    onClick={() => setIsConfirmActivateOpen(true)}
                  >
                    <i className="fas fa-play mr-2"></i>
                    Activate Device
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </Card>
      
      {/* Activate Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isConfirmActivateOpen}
        title="Activate Device"
        message={`Are you sure you want to activate this ${device?.provider || ''} device?`}
        confirmLabel="Activate"
        confirmVariant="primary"
        cancelLabel="Cancel"
        isLoading={isActionLoading}
        onConfirm={handleActivate}
        onCancel={() => setIsConfirmActivateOpen(false)}
      />
      
      {/* Deactivate Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isConfirmDeactivateOpen}
        title="Deactivate Device"
        message={`Are you sure you want to deactivate this ${device?.provider || ''} device?`}
        confirmLabel="Deactivate"
        confirmVariant="danger"
        cancelLabel="Cancel"
        isLoading={isActionLoading}
        onConfirm={handleDeactivate}
        onCancel={() => setIsConfirmDeactivateOpen(false)}
      />
    </div>
  );
};

export default DeviceDetails;